import React, { useState } from 'react';
import api from '../services/api';

interface Location {
  latitude: number;
  longitude: number;
}

interface CourseSearchPickerProps {
  value?: Location;
  onChange?: (location: Location) => void;
}

export default function CourseSearchPicker({ value, onChange }: CourseSearchPickerProps) {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await api.get('/geocode/search', { params: { q } });
      setResults(res.data || []);
    } catch (e) {
      console.error(e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const select = (item: any) => {
    const loc = { latitude: item.lat, longitude: item.lon };
    onChange?.(loc);
  };

  return (
    <div className="mt-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Search golf course (name, city/state)
      </label>
      <div className="flex space-x-2 mb-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoCorrect="off"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter course name or location..."
        />
        <button
          type="button"
          onClick={search}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          disabled={!q.trim() || loading}
        >
          Search
        </button>
      </div>

      {loading ? (
        <div className="mt-2 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Searching...</span>
        </div>
      ) : (
        <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
          {results.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {results.map((item, i) => (
                <div
                  key={i}
                  className="p-3 hover:bg-gray-50 cursor-pointer flex items-start"
                  onClick={() => select(item)}
                >
                  <div className="text-gray-600 mr-3 mt-0.5">📍</div>
                  <div className="flex-1">
                    <div className="font-medium">{item.name}</div>
                    <div className="text-sm text-gray-600">
                      Lat: {item.lat.toFixed(6)}  Lon: {item.lon.toFixed(6)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : q.trim() && !loading ? (
            <div className="p-3 text-gray-500 text-center">No results found.</div>
          ) : (
            <div className="p-3 text-gray-400 text-center text-sm">Search for a course to get started.</div>
          )}
        </div>
      )}

      {value?.latitude && value?.longitude ? (
        <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-md">
          <div className="text-sm text-green-700">
            Selected: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
          </div>
        </div>
      ) : null}
    </div>
  );
}
